import { useState } from "react";
import { motion } from "framer-motion";
import { Palette, RotateCcw } from "lucide-react";

const PALETTE = ["#FFC107", "#FF6B6B", "#38BDF8", "#4ADE80", "#C084FC", "#F8F5EC"];

const TryItNow = () => {
  const [color, setColor] = useState(PALETTE[0]);
  const [fills, setFills] = useState({ arch1: "#fff", arch2: "#fff", arch3: "#fff", star: "#fff", cloud: "#fff" });
  const paint = (part) => setFills((f) => ({ ...f, [part]: color }));
  const reset = () => setFills({ arch1: "#fff", arch2: "#fff", arch3: "#fff", star: "#fff", cloud: "#fff" });

  return (
    <section
      id="try"
      className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden py-24"
      data-testid="tryitnow-section"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 top-16 h-80 w-80 rounded-full bg-[#FEF3C7] blur-3xl" />
        <div className="absolute -right-24 bottom-16 h-80 w-80 rounded-full bg-[#DCFCE7] blur-3xl" />
      </div>
      <div className="relative mx-auto w-full max-w-2xl px-4 sm:px-6">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#16A34A]">Little artists</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Try it now
          </h2>
          <p className="mx-auto mt-3 max-w-md text-base text-slate-600">
            A tiny taste of the activity library — pick a colour and tap the rainbow.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-10 rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_20px_40px_-10px_rgba(14,165,233,0.15)]"
          data-testid="coloring-canvas-widget"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-bold text-slate-900">Colour the rainbow</h3>
            <button
              onClick={reset}
              data-testid="coloring-reset-btn"
              aria-label="Reset colouring"
              className="grid h-9 w-9 place-items-center rounded-full bg-[#F8F5EC] text-slate-500 transition-transform hover:rotate-[-90deg]"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>

          <svg viewBox="0 0 300 200" className="mt-6 w-full" data-testid="coloring-svg">
            <path d="M40 170 A110 110 0 0 1 260 170" fill="none" stroke={fills.arch3} strokeWidth="26" strokeLinecap="round" className="cursor-pointer transition-all hover:opacity-80" onClick={() => paint("arch3")} />
            <path d="M62 170 A88 88 0 0 1 238 170" fill="none" stroke={fills.arch2} strokeWidth="24" strokeLinecap="round" className="cursor-pointer transition-all hover:opacity-80" onClick={() => paint("arch2")} />
            <path d="M82 170 A68 68 0 0 1 218 170" fill="none" stroke={fills.arch1} strokeWidth="22" strokeLinecap="round" className="cursor-pointer transition-all hover:opacity-80" onClick={() => paint("arch1")} />
            <g stroke="#1E293B" strokeWidth="2" strokeLinejoin="round">
              <path d="M40 170 A110 110 0 0 1 260 170" fill="none" opacity="0.15" />
              <polygon points="150,14 157,32 176,32 161,43 167,61 150,50 133,61 139,43 124,32 143,32" fill={fills.star} className="cursor-pointer transition-all hover:opacity-80" onClick={() => paint("star")} />
              <path d="M250 165 q0 -18 18 -18 q6 -14 20 -8 q14 -6 18 8 q14 4 8 18 z" transform="translate(-230,0)" fill={fills.cloud} className="cursor-pointer transition-all hover:opacity-80" onClick={() => paint("cloud")} />
            </g>
          </svg>

          <div className="mt-4 flex items-center justify-center gap-2.5" data-testid="coloring-palette">
            <Palette className="h-4 w-4 text-slate-400" />
            {PALETTE.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                data-testid={`palette-${c.replace("#", "")}`}
                aria-label={`Pick colour ${c}`}
                className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${
                  color === c ? "border-slate-900 scale-110" : "border-white shadow"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TryItNow;
